import { type MutableRefObject, useMemo, useRef } from 'react'
import type { PageViewMode } from '@/components/pages/home'
import type { Guide } from '@/lib/guides/types/guide'
import { type LayoutSnapLines, extractLayoutSnapLines } from '@/lib/guides/smart-guides'
import { computeGeneratedGridSnapGuides, computeMarginSnapGuides } from '../helpers/snap-guides'

/**
 * Everything objects snap to: the ruler guides (their live drag positions mid-drag), the column/
 * row grid's generated guides and the page margins — plus the layout lines smart guides derive
 * from them, mirrored into `layoutSnapLinesForDragCreateRef` for the shape drag-create gesture.
 */
export function useSnapGuides({
  guides,
  localDragGuides,
  layoutSnapLinesForDragCreateRef,
  pageTrimWidth,
  trimWidth,
  trimHeight,
  bleedMargin,
  marginTop,
  marginBottom,
  marginInside,
  marginOutside,
  showLeftPageGuides,
  showRightPageGuides,
  columns,
  rows,
  colGutter,
  rowGutter,
  pageViewMode,
}: {
  guides: Guide[]
  localDragGuides: Guide[] | null
  layoutSnapLinesForDragCreateRef: MutableRefObject<LayoutSnapLines>
  pageTrimWidth: number
  trimWidth: number
  trimHeight: number
  bleedMargin: number
  marginTop: number
  marginBottom: number
  marginInside: number
  marginOutside: number
  showLeftPageGuides: boolean
  showRightPageGuides: boolean
  columns: number
  rows: number
  colGutter: number
  rowGutter: number
  pageViewMode: PageViewMode
}) {
  /**
   * Ref mirror of the `snapGuides` memo (below). Updated each render so that the unified-move
   * handlers (declared before `snapGuides`) can read the latest guides without a
   * temporal-dead-zone issue.
   */
  const snapGuidesRef = useRef<ReadonlyArray<Guide>>([])

  const generatedGridSnapGuides = useMemo<Guide[]>(
    () =>
      computeGeneratedGridSnapGuides({
        pageTrimWidth,
        bleedMargin,
        trimHeight,
        marginTop,
        marginBottom,
        marginInside,
        marginOutside,
        showLeftPageGuides,
        showRightPageGuides,
        columns,
        rows,
        colGutter,
        rowGutter,
        pageViewMode,
      }),
    [
      pageTrimWidth,
      bleedMargin,
      trimHeight,
      marginTop,
      marginBottom,
      marginInside,
      marginOutside,
      showLeftPageGuides,
      showRightPageGuides,
      columns,
      rows,
      colGutter,
      rowGutter,
      pageViewMode,
    ]
  )

  const marginSnapGuides = useMemo<Guide[]>(
    () =>
      computeMarginSnapGuides({
        trimWidth,
        pageTrimWidth,
        trimHeight,
        bleedMargin,
        marginTop,
        marginBottom,
        marginInside,
        marginOutside,
        pageViewMode,
      }),
    [
      trimWidth,
      pageTrimWidth,
      trimHeight,
      bleedMargin,
      marginTop,
      marginBottom,
      marginInside,
      marginOutside,
      pageViewMode,
    ]
  )

  const visibleGuides = localDragGuides ?? guides

  const snapGuides = useMemo(() => {
    return [...visibleGuides, ...generatedGridSnapGuides, ...marginSnapGuides]
  }, [visibleGuides, generatedGridSnapGuides, marginSnapGuides])

  snapGuidesRef.current = snapGuides

  const layoutSnapLinesForSmartGuides = useMemo(
    () => extractLayoutSnapLines(snapGuides),
    [snapGuides]
  )

  layoutSnapLinesForDragCreateRef.current = layoutSnapLinesForSmartGuides

  return { visibleGuides, snapGuides }
}
